import React, { useEffect, useRef, useState } from 'react';
import { PauseIcon, PlayIcon } from 'lucide-react';
import { CountdownRing } from './CountdownRing';
import { StepNav } from './StepNav';
import { PlaybackProvider } from '../hooks/usePlayback';
import { ScreenPromise } from './screens/ScreenPromise';
import { ScreenQuestion } from './screens/ScreenQuestion';
import { ScreenProblem } from './screens/ScreenProblem';
import { ScreenSolution } from './screens/ScreenSolution';
import { ScreenReward } from './screens/ScreenReward';
import { BRAND, BRAND_LOGO, TOTAL_SECONDS } from '../data/teaser';
type Screen = 'promise' | 'question' | 'problem' | 'solution' | 'reward';
const CHAPTERS: Screen[] = ['question', 'problem', 'solution', 'reward'];
const LABELS: Record<Screen, string> = {
  promise: 'The promise',
  question: 'Your take',
  problem: 'The challenge',
  solution: 'The solution',
  reward: 'The reward'
};
const TICK_MS = 100;

/** The original, product-led edition of the teaser. */
export function OriginalTeaser() {
  const [screen, setScreen] = useState<Screen>('promise');
  const [remaining, setRemaining] = useState<number>(TOTAL_SECONDS);
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!running || paused) return;
    intervalRef.current = setInterval(() => {
      setRemaining(prev => Math.max(0, prev - TICK_MS / 1000));
    }, TICK_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, paused]);

  // The clock simply stops at zero — it never moves the user on by itself
  useEffect(() => {
    if (running && remaining <= 0) setRunning(false);
  }, [running, remaining]);
  const start = () => {
    setPaused(false);
    setRunning(true);
    setScreen('question');
  };
  const goTo = (next: Screen) => {
    if (next === 'reward') {
      setRunning(false);
      setRemaining(0);
    } else if (next !== 'promise' && remaining > 0) {
      setRunning(true);
    }
    setScreen(next);
  };
  const restart = () => {
    setRunning(false);
    setPaused(false);
    setRemaining(TOTAL_SECONDS);
    setScreen('promise');
  };
  const showChrome = screen !== 'promise';
  const showPauseButton = screen !== 'promise' && screen !== 'reward';
  const chapterIndex = CHAPTERS.indexOf(screen);
  const nav = (() => {
    switch (screen) {
      case 'question':
        return null;
      case 'problem':
        return <StepNav backLabel={LABELS.question} onBack={() => goTo('question')} nextLabel={`See ${LABELS.solution.toLowerCase()}`} onNext={() => goTo('solution')} data-id="element-27" data-mp="k1tsvjb6d2oq" />;
      case 'solution':
        return <StepNav backLabel={LABELS.problem} onBack={() => goTo('problem')} nextLabel={`See ${LABELS.reward.toLowerCase()}`} onNext={() => goTo('reward')} data-id="element-28" data-mp="2qtujceilqz7m" />;
      case 'reward':
        return <StepNav backLabel={LABELS.solution} onBack={() => goTo('solution')} onRestart={restart} data-id="element-29" data-mp="1cze3k1dlz2oq" />;
      default:
        return null;
    }
  })();
  return <div className="flex h-full w-full flex-col bg-solventum-bg px-6 pb-3 pt-2 selection:bg-solventum-primary selection:text-solventum-bg" data-id="element-30" data-mp="142lhwtsy6tir">
      {showChrome && <header className="flex w-full flex-shrink-0 items-center justify-between pb-2 pt-3" data-id="element-31" data-mp="1vvm08gsk258f">
          <div className="flex flex-col" data-id="element-32" data-mp="1rv22bb05qxl8">
            <img src={BRAND_LOGO} alt={BRAND} className="h-7 w-auto object-contain" data-id="element-33" data-mp="1mvh1ev1fgf5v" />
            <span className="mt-1 font-body text-[11px] tracking-wide text-white/45" data-id="element-34" data-mp="1hvb8lbjf8mm7">
              {LABELS[screen]}
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-35" data-mp="bdxvuw0dk045">
            {showPauseButton && <button onClick={() => setPaused(prev => !prev)} aria-pressed={paused} aria-label={paused ? 'Resume the teaser' : 'Pause the teaser'} className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/80 transition-colors duration-150 hover:border-solventum-primary hover:text-solventum-primary" data-id="element-36" data-mp="6jafjkfiyc0n">
                {paused ? <PlayIcon className="h-4 w-4" data-id="element-37" data-mp="2e37inq29sn2z" /> : <PauseIcon className="h-4 w-4" data-id="element-38" data-mp="2i3zt2mmvoy0u" />}
              </button>}
            <CountdownRing remaining={remaining} stopped={screen === 'reward'} paused={paused} data-id="element-39" data-mp="244hzzv2juylo" />
          </div>
        </header>}

      <main className="relative min-h-0 flex-1 overflow-hidden" data-id="element-40" data-mp="so0fmtc88mfe">
        <PlaybackProvider paused={paused} data-id="element-41" data-mp="sqmiwgs34frt">
          <div key={screen} className="h-full w-full animate-push-in" data-id="element-42" data-mp="xnwi2o5a05su">
            {screen === 'promise' && <ScreenPromise onStart={start} data-id="element-43" data-mp="q3c791qsrbfu" />}
            {screen === 'question' && <ScreenQuestion onAnswer={() => goTo('problem')} data-id="element-44" data-mp="l5k3nagsi0vt" />}
            {screen === 'problem' && <ScreenProblem data-id="element-45" data-mp="z5bmppm5y7go" />}
            {screen === 'solution' && <ScreenSolution data-id="element-46" data-mp="z2ph3viu2213" />}
            {screen === 'reward' && <ScreenReward data-id="element-47" data-mp="u5guniqnjbu1" />}
          </div>
        </PlaybackProvider>
      </main>

      {nav}

      {chapterIndex >= 0 && <div className="flex w-full flex-shrink-0 items-center justify-center gap-1.5 pt-3" aria-hidden="true" data-id="element-48" data-mp="6gnv7catmluo">
          {CHAPTERS.map((chapter, index) => <span key={chapter} className={`h-1.5 rounded-full transition-all duration-300 ${index === chapterIndex ? 'w-5 bg-solventum-primary' : 'w-1.5 bg-white/20'}`} data-id="element-49" data-mp="g6pjo57366a4" data-mpi={index} />)}
        </div>}

      <footer className="w-full flex-shrink-0 pt-2 text-center" data-id="element-50" data-mp="3evo4yq6717tl">
        <p className="font-body text-[11px] tracking-wide text-white/30" data-id="element-51" data-mp="1itkyq7ttcuf0">
          © Solventum 2026
        </p>
      </footer>
    </div>;
}